'use client';

import { ChangeEvent, useEffect } from 'react';
import {
  BANK_ICONS,
  creditCardMask,
  getApiError,
  months,
  securityCodeMask,
  zipCodeMask
} from 'constant';
import { FormikValues } from 'formik';
import Image from 'next/image';
import { useGetStatesQuery } from 'services/locationsApi';

import {
  Box,
  Flex,
  Stack,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch } from 'store/hooks';
import { updateGuestZip } from 'store/reducers/persistGuestAuthSlice';

import {
  getCardType,
  getYearOptions,
  transformToReactSelectOptions
} from 'utils';

import { Loader, NoWrap, SelectField, Text, TextField } from 'ui';

const initialValues: FormikValues = {
  card_number: '',
  card_cvv2: '',
  exp_month: '',
  exp_year: '',
  owner: '',
  addr_ln1: '',
  city: '',
  state: '',
  zip: ''
};

interface AddCardFormProps {
  formikData: FormikValues;
  borderRadius?: string | number;
  errorMsg: string;
  clearErrors: () => void;
  isGuestFlow?: boolean;
}

const AddCardForm = ({
  formikData,
  borderRadius = '6px',
  errorMsg = '',
  isGuestFlow = false,
  clearErrors = () => {}
}: AddCardFormProps) => {
  const dispatch = useAppDispatch();
  const { data: states = [], isLoading: isStatesLoading } =
    useGetStatesQuery('') ?? {};

  const {
    colors: {
      palette: {
        components: { newCardForm }
      }
    }
  } = useTheme();
  const inputSize: 'lg' | 'xl' | undefined = useBreakpointValue(
    { base: 'lg', lg: 'xl' },
    { fallback: 'xl' }
  );
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );

  const {
    handleSubmit,
    getFieldProps,
    validateField,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    values,
    isValid
  } = formikData;

  const yearsOptions = getYearOptions();
  const formattedStates = transformToReactSelectOptions(states);
  const bank = getCardType(values.card_number);
  const bankIcon = bank ? BANK_ICONS[bank] : '';

  const handleClearError = () => {
    clearErrors();
  };

  const getError = (fieldName: string) => errors[fieldName];

  const resetFieldHandler = (field: string) => () => {
    setFieldValue(field, initialValues[field]);
  };

  const handleSelectChange = async (name: string, value: string | number) => {
    await setFieldValue(name, value);
    if (!isValid || Boolean(errorMsg.length)) {
      handleClearError();
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value }
    } = event;
    setFieldValue(name, value);
    if (!isValid || Boolean(errorMsg.length)) {
      handleClearError();
    }
  };

  useEffect(() => {
    if (isGuestFlow && values.zip) {
      dispatch(updateGuestZip(values.zip));
    }
    return () => {
      handleClearError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isGuestFlow, values.zip]);

  return (
    <Box position='relative' as='form' onSubmit={handleSubmit}>
      <Stack
        borderTopRadius={borderRadius}
        borderBottomRadius='6px'
        px={['25px', '15px', '25px', '50px']}
        py={['25px', '30px']}
        bg={newCardForm.bg}
      >
        <Flex
          alignItems='center'
          justifyContent='space-between'
          gap={{ lg: '15px', xl: '25px' }}
          w='100%'
          mb={{ base: '20px', md: '30px' }}
        >
          <TextField
            autoFocus
            {...getFieldProps('card_number')}
            error={getApiError('card_number', errorMsg as string)}
            touched={!!touched.card_number}
            onReset={resetFieldHandler('card_number')}
            label='Credit Card Number'
            isFieldValue={!!values.card_number}
            onChange={handleInputChange}
            size={inputSize}
            width={{ base: '100%', lg: '80%' }}
            rightElementProps={{
              sx: {
                width: { base: '38px', lg: '80px' },
                svg: { width: { lg: '30px' }, height: { lg: '30px' } }
              }
            }}
            inputStyles={{ paddingRight: { lg: '80px' } }}
            containerStyles={{
              '& label': { fontSize: '12px' }
            }}
            mask={creditCardMask}
            fontSize={{
              base: '22px',
              lg: '40px',
              xl: '50px'
            }}
          />

          {bankIcon && (
            <Flex
              display={{ base: 'none', lg: 'flex' }}
              width={{ base: '0%', lg: '20%' }}
              justifyContent='flex-end'
            >
              <Box
                width='160px'
                height='117px'
                justifyContent='center'
                mt='20px'
                position='relative'
                sx={{
                  img: {
                    width: 'auto !important'
                  }
                }}
              >
                <Image src={bankIcon} alt='Credit card' fill sizes='100px' />
              </Box>
            </Flex>
          )}
        </Flex>

        <Flex flexWrap='wrap' flexDirection={{ base: 'column', lg: 'row' }}>
          <Flex
            w={{ base: '100%', lg: '50%' }}
            pr={{ base: '0', lg: '20px', xl: '30px' }}
            pb={{ base: '30px', lg: '0' }}
            borderRight={{
              base: 'none',
              lg: `1px solid ${newCardForm.borderColor}`
            }}
            borderBottom={{
              base: `1px solid ${newCardForm.borderColor}`,
              lg: 'none'
            }}
            direction='column'
            gap='15px'
          >
            <TextField
              {...getFieldProps('owner')}
              label='PERSON’S NAME ON CARD'
              onChange={handleInputChange}
              error={getError('owner')}
              touched={!!touched.owner}
              onReset={resetFieldHandler('owner')}
              isFieldValue={!!values.owner}
              size='lg'
              containerStyles={{
                '& label': { fontSize: '12px' }
              }}
              fontSize='21px'
            />
            <Flex
              flexWrap='wrap'
              gap='15px'
              justifyContent='space-between'
              flexDirection={{ base: 'row-reverse', lg: 'row' }}
            >
              <Flex
                gap='16px'
                width='100%'
                flexDirection={{
                  base: 'column',
                  sm: 'row'
                }}
                alignItems='flex-end'
              >
                <SelectField
                  name='exp_month'
                  width={{ base: '100%', sm: '100%' }}
                  setFieldValue={handleSelectChange}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  value={values.exp_month}
                  options={months}
                  error={getApiError('exp_month', errorMsg as string)}
                  touched={!!touched.exp_month}
                  isSearchable
                  bordered
                  placeholder={isMobile ? 'Select month' : ''}
                  label='Expiration  Date'
                  stylesControl={{ height: '60px', fontSize: '21px' }}
                  stylesOption={{ fontSize: '21px' }}
                  formControlProps={{
                    sx: {
                      '& label': { fontSize: '12px' }
                    }
                  }}
                />

                <SelectField
                  name='exp_year'
                  width={{ base: '100%', sm: '70%' }}
                  setFieldValue={handleSelectChange}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  value={values.exp_year}
                  options={yearsOptions}
                  error={getApiError('exp_year', errorMsg as string)}
                  touched={!!touched.exp_year}
                  isSearchable
                  bordered
                  placeholder={isMobile ? 'Select year' : ''}
                  stylesControl={{ height: '60px', fontSize: '21px' }}
                  stylesOption={{ fontSize: '21px' }}
                  formControlProps={{
                    sx: {
                      '& label': { fontSize: '12px' }
                    }
                  }}
                />
              </Flex>

              <Flex
                gap={{ base: '16px', sm: '29px' }}
                width='100%'
                alignItems='center'
                flexDirection={{ base: 'column', sm: 'row' }}
              >
                <TextField
                  {...getFieldProps('card_cvv2')}
                  label='Security Code'
                  error={getError('card_cvv2')}
                  onChange={handleInputChange}
                  touched={!!touched.card_cvv2}
                  onReset={resetFieldHandler('card_cvv2')}
                  isFieldValue={!!values.card_cvv2}
                  width={{ base: '100%', md: 'calc(40% - 8px)' }}
                  type='password'
                  mask={securityCodeMask}
                  size='lg'
                  containerStyles={{
                    '& label': { fontSize: '12px' }
                  }}
                  fontSize='21px'
                />

                <Text
                  fontSize={['12px', '15px']}
                  color={newCardForm.secureMsg}
                  as='span'
                  fontWeight='regular'
                  textTransform='uppercase'
                  lineHeight={['17px', '20px']}
                  maxW={{ base: '90%', sm: '256px' }}
                  pt={{ base: 0, sm: '20px' }}
                  textAlign={{ base: 'center', sm: 'left' }}
                >
                  Security Code is used for card verification and{' '}
                  <NoWrap>never stored</NoWrap>
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            w={{ base: '100%', lg: '50%' }}
            pl={{ base: '0', lg: '20px', xl: '30px' }}
            pt={{ base: '30px', lg: '0' }}
            gap='15px'
            flexWrap='wrap'
          >
            <TextField
              {...getFieldProps('addr_ln1')}
              label='Billing Address'
              onChange={handleInputChange}
              error={getError('addr_ln1')}
              touched={!!touched.addr_ln1}
              onReset={resetFieldHandler('addr_ln1')}
              isFieldValue={!!values.addr_ln1}
              w='100%'
              size='lg'
              containerStyles={{
                '& label': { fontSize: '12px' }
              }}
              fontSize='21px'
            />

            <TextField
              {...getFieldProps('city')}
              label='City'
              onChange={handleInputChange}
              error={getError('city')}
              touched={!!touched.city}
              onReset={resetFieldHandler('city')}
              isFieldValue={!!values.city}
              w='100%'
              size='lg'
              containerStyles={{
                '& label': { fontSize: '12px' }
              }}
              fontSize='21px'
            />

            <Flex
              gap='16px'
              width={{ base: '100%', md: 'calc(76.6% - 10px)', lg: '100%' }}
            >
              <Box w={{ base: '66%', sm: '100%' }}>
                <SelectField
                  name='state'
                  setFieldValue={handleSelectChange}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  value={values.state}
                  options={formattedStates}
                  error={getError('state')}
                  touched={!!touched.state}
                  isSearchable
                  bordered
                  placeholder=''
                  label='State'
                  width='100%'
                  stylesControl={{ height: '60px', fontSize: '21px' }}
                  stylesOption={{ fontSize: '21px' }}
                  formControlProps={{
                    sx: {
                      '& label': { fontSize: '12px' }
                    }
                  }}
                />
              </Box>

              <TextField
                {...getFieldProps('zip')}
                label='Zip Code'
                onChange={handleInputChange}
                error={getError('zip')}
                touched={!!touched.zip}
                onReset={resetFieldHandler('zip')}
                isFieldValue={!!values.zip}
                width={['40%', '70%']}
                mask={zipCodeMask}
                marginBottom='5px'
                size='lg'
                inputStyles={{
                  paddingRight: '20px'
                }}
                containerStyles={{
                  '& label': { fontSize: '12px' }
                }}
                fontSize='21px'
              />
            </Flex>
          </Flex>

          {errorMsg && (
            <Text w='100%' textAlign='center' color='red.300' mt='20px'>
              {errorMsg as string}
            </Text>
          )}
        </Flex>
      </Stack>

      {isStatesLoading && <Loader fixed bg={newCardForm.loaderBg} />}
    </Box>
  );
};

export default AddCardForm;
